import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, CardBody, ButtonGroup, CardHeader, Button} from "reactstrap";
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { createSelector } from "reselect";
import { useSelector, useDispatch } from "react-redux";
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { viewEventSongs} from "../../slices/event/view/thunk";
import {authUserManager } from "../../helpers/api_helper";
import {deleteEventSong} from "../../slices/event/song/delete/thunk";
import {eventSongDeleteReset} from "../../slices/event/song/delete/reducer";
import {addSongGuest} from "../../slices/event/song/add/thunk";
import {addDedication, deleteDedication} from "../../slices/event/dedication/thunk";
import {voteSongGuest} from "../../slices/event/song/vote/thunk";
import {appName, typeKaraoke} from "../../Components/constants/default";
import {getEventDetails} from "../../slices/event/details/thunk";
import {notificationService} from "../../helpers/NotificationService";
import {AddEventSongModal} from "../../Components/Modals/AddEventSongModal";
import {VoteModal} from "../../Components/Modals/VoteModal";
import {EditSongNoteModal} from "../../Components/Modals/EditSongNoteModal";
import {EditEventSongModal} from "../../Components/Modals/EditEventSongModal";
import {SongNotesModal} from "../../Components/Modals/SongNotesModal";
import {SongFullNameModal} from "../../Components/Modals/SongFullNameModal";
import {AddDedicationModal} from "../../Components/Modals/AddDedicationModal";
import {useUserColors} from "../../Components/Hooks/useColors";
import AddSongButton from "../../Components/Buttons/AddSongButton";
import StartEventButton from "../../Components/Buttons/StartEventButton";
import EventClosedAlert from "../../Components/Common/EventClosedAlert";
import VoteSongButton from "../../Components/Buttons/VoteSongButton";
import EventHeader from "../../Components/EventHeader";
import Spinner from "../../Components/Common/Spinner";
import {reorderEventSongs} from "../../slices/event/song/reorder/thunk";
import {eventSongReorderReset} from "../../slices/event/song/reorder/reducer";
import TipHostModal from "../../Components/Modals/TipHostModal";
import { useStripe } from '@stripe/react-stripe-js';
import { verifyPendingTipPayment } from "../../slices/tip/thunk";
import {
  savePendingTipAction,
  loadPendingTipAction,
  clearPendingTipAction,
} from "../../helpers/pendingTipAction";
import {RegisterGuestModal} from "../../Components/Modals/RegisterGuestModal";

// Na telefonie nazwa utworu bywa ucięta. Gdy tekst nie mieści się w wierszu, na jego
// końcu pokazujemy inline "...więcej" (nie zmienia szerokości ani nie łamie wiersza);
// kliknięcie otwiera modal z pełną nazwą. Tak samo dla gościa i dla hosta.
const SongTextTruncate = ({ children, fullText, onShowFull }) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const check = () => {
      const el = ref.current;
      if (!el) return;
      setTruncated(el.scrollWidth > el.clientWidth + 1);
    };
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [fullText]);

  return (
    <div className="d-flex align-items-baseline" style={{ minWidth: 0 }}>
      <div
        ref={ref}
        className="text-container-mobile"
        style={{
          minWidth: 0,
          flex: '0 1 auto',
          textOverflow: 'clip',
          // Ostatnie znaki gasną, żeby ucięty tytuł nie zlewał się z "...więcej".
          ...(truncated ? {
            maskImage: 'linear-gradient(to right, #000 calc(100% - 2.5rem), transparent)',
            WebkitMaskImage: 'linear-gradient(to right, #000 calc(100% - 2.5rem), transparent)',
          } : {}),
        }}
      >
        {children}
      </div>
      {truncated && (
        <span
          className="text-white text-decoration-underline flex-shrink-0 ms-1"
          style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
          onClick={() => onShowFull(fullText)}
        >
          ...{t('more')}
        </span>
      )}
    </div>
  );
};

const EventSongs = () => {
  const { eventId } = useParams();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isClubAdmin = authUserManager.isClubAdmin();
  const authAdminId = authUserManager.getAdminId();
  const isAdmin = authUserManager.isAdmin();
  let guestId = null;
  let isEventGuest = false;

  useEffect(() => {
    dispatch(getEventDetails(eventId));
  }, []);

  //Event Details
  const selectEventDetails = createSelector(
    (state) => state.Event,
    (slice) => ({
      event: slice.event,
    })
  );
  const { event } = useSelector(selectEventDetails);

  const eventName = event ? (event.name === 'Your event name' ? t(event.name) : event.name) : '';
  const type = event?.type;
  const adminId = event?.clubAdmin?.id;
  const startAt = event?.startAt;
  const allowAddSongs = event?.allowAddSongs;
  const allowTips = event?.allowTips;
  // Wymóg napiwku obowiązuje tylko gdy napiwki są włączone (chroni przed zakleszczeniem).
  const requireTip = Boolean(event?.requireTip && event?.allowTips);
  const minTipAmount = event?.minTipAmount || 10;
  // Dedykacje bez utworu — karaoke ich nie ma (tam notatka jedzie z utworem).
  const allowDedications = Boolean(event?.allowDedications) && type !== typeKaraoke;
  const isEventAdmin = authUserManager.getAdminId() === adminId;
  const adminUsername = event?.clubAdmin?.username;

  document.title = eventName ? eventName + ' | ' + appName : ''
  useUserColors();
  if (!isClubAdmin) {
    guestId = authUserManager.getEventGuestId(eventId);
    isEventGuest = authUserManager.isEventGuest(eventId, startAt);
  }

  // Modal rejestracji gościa - automatycznie wyświetlany przy wejściu
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const togRegisterModal = () => {
    setIsRegisterModalOpen(!isRegisterModalOpen);
  };

  // Sprawdź przy wejściu czy user jest gościem i czy event jest aktywny
  useEffect(() => {
    if (event && Object.keys(event).length > 0) {
      const shouldShowRegister = !isClubAdmin && !isEventGuest && startAt;
      if (shouldShowRegister) {
        setIsRegisterModalOpen(true);
      }
    }
  }, [event, isClubAdmin, isEventGuest, startAt]);

  // Monitoruj sukces rejestracji aby zaktualizować status gościa
  const loginGuestSuccess = useSelector(state => state.Profile.loginGuestSuccess);
  useEffect(() => {
    if (loginGuestSuccess) {
      // Odśwież status gościa
      guestId = authUserManager.getEventGuestId(eventId);
      isEventGuest = authUserManager.isEventGuest(eventId, startAt);
      setIsRegisterModalOpen(false);
    }
  }, [loginGuestSuccess]);

  useEffect(() => {
    if (!isEventAdmin) return;

    const askedForPermission = sessionStorage.getItem('notificationPromptShown');

    if ('Notification' in window && Notification.permission === 'default' && !askedForPermission) {
      // Pokaż toast z przyciskiem do włączenia
      notificationService.custom(
        <div className="d-flex align-items-start">
          <div className="me-3">
            <div className="rounded-circle bg-warning-subtle d-flex align-items-center justify-content-center" style={{ width: 42, height: 42 }}>
              <i className="ri-notification-3-line text-warning"></i>
            </div>
          </div>

          <div className="flex-grow-1">
            <h5 className="mb-2">
              {t('Stay in the loop')}!
            </h5>

            <p className="mb-2 text-muted">
              {t('Enable notifications to be alerted')}:
            </p>

            <ul className="mb-3 text-muted ps-3">
              <li><h6>{t('when someone sends you a tip.')}</h6></li>
              <li><h6>{t('when new songs appear on your list.')}</h6></li>
            </ul>

            <div className="d-flex gap-2">
              <Button
                className="fw-semibold color-primary"
                onClick={() => {
                  Notification.requestPermission().then((permission) => {
                    notificationService.dismissAll();
                    if (permission === 'granted') {
                      notificationService.success(t('Notifications enabled!'));
                    }
                  });
                }}
              >
                <i className="ri-notification-line me-1"></i>
                {t('Enable')}
              </Button>
            </div>
          </div>
        </div>,
        {
          autoClose: false,
          position: 'top-center',
          className: 'bg-body-secondary text-white border-0 shadow-lg rounded-3',
          hideProgressBar: true,
          closeButton: true,
        }
      );
      sessionStorage.setItem('notificationPromptShown', 'true');
    }
  }, [isEventAdmin, t]);

  useEffect(() => {
    if (!isEventAdmin) return;

    const mercureUrl = new URL(process.env.REACT_APP_MERCURE_PUBLIC_URL);
    mercureUrl.searchParams.append('topic', `events/${eventId}/tip`);

    const eventSourceTip = new EventSource(mercureUrl.toString());

    eventSourceTip.onmessage = (event) => {
      const data = JSON.parse(event.data);

      const amountPln = data.amount
        ? data.amount
        : '?';

      const notification = <div><strong>{data.guestName || t('Someone')} </strong> {t('has sent you a tip of')} <strong> {amountPln} </strong>zł!</div>
      notificationService.custom(notification,{
          position: 'top-center',
          autoClose: false, className: 'bg-success text-white border-0 shadow-lg rounded-3',
          closeButton: true,
        }
      );

      if ('Notification' in window) {
        if (Notification.permission === 'granted') {
          new Notification(t('You received a tip!'), {
            body: (data.guestName || t('Someone')) + ' ' + t('has sent you a tip of') + ' ' +  amountPln + 'zł!',
            icon: '/logo512.png',
          });
        }
      }
    };

    return () => eventSourceTip.close();
  }, [eventId, isEventAdmin, t]);

  useEffect(() => {
    const mercureUrl = new URL(process.env.REACT_APP_MERCURE_PUBLIC_URL);
    mercureUrl.searchParams.append('topic', `event_song/${eventId}/add`);

    const eventSource = new EventSource(mercureUrl.toString());

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('New song on your list!', data);
      if (
        (authUserManager.getEventGuestId(eventId) === data.userId) ||
        (authAdminId === data.userId)
      ) return;
      dispatch(viewEventSongs(eventId));
      if (isEventAdmin && 'Notification' in window) {
        console.log('not', data);
        if (Notification.permission === 'granted') {
          console.log('granty', data);
          new Notification(t('New song on your list!'), {
            body: data.song,
            icon: '/logo512.png',
          });
        }
      }
    };

    return () => eventSource.close();
  }, [eventId, dispatch, isEventAdmin, authAdminId]);

  useEffect(() => {
    const mercureUrl = new URL(process.env.REACT_APP_MERCURE_PUBLIC_URL);
    const topicsList = [
      `event_song/${eventId}/remove`,
      `event_song/${eventId}/reorder`,
      `event_song/${eventId}/update`
    ];
    topicsList.forEach(topic => {
      mercureUrl.searchParams.append('topic', topic);
    });

    const eventSource = new EventSource(mercureUrl.toString());

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (
        (authUserManager.getEventGuestId(eventId) === data.userId) ||
        (authAdminId === data.userId)
      ) return;

      dispatch(viewEventSongs(eventId));
    };

    return () => eventSource.close();
  }, [eventId, dispatch, authAdminId]);

  useEffect(() => {
    const mercureUrl = new URL(process.env.REACT_APP_MERCURE_PUBLIC_URL);
    const topicsList = [
      `events/${eventId}/end`,
      `events/${eventId}/start`
    ]
    topicsList.forEach(topic => {
      mercureUrl.searchParams.append('topic', topic);
    });
    const eventSourceEventStartEnd = new EventSource(mercureUrl.toString());

    eventSourceEventStartEnd.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (authAdminId === data.userId) return;
      dispatch(getEventDetails(eventId));
      dispatch(viewEventSongs(eventId));
    };

    return () => {
      eventSourceEventStartEnd.close();
    };
  }, [eventId, dispatch]);

  useEffect(() => {
    const mercureUrl = new URL(process.env.REACT_APP_MERCURE_PUBLIC_URL);
    const topicsList = [
      `events/${eventId}/update`,
    ]
    topicsList.forEach(topic => {
      mercureUrl.searchParams.append('topic', topic);
    });
    const eventSourceEventUpdate = new EventSource(mercureUrl.toString());

    eventSourceEventUpdate.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (isClubAdmin && authAdminId === data.userId) return;
      dispatch(getEventDetails(eventId));
      dispatch(viewEventSongs(eventId));
    };

    return () => {
      eventSourceEventUpdate.close();
    };
  }, [eventId, dispatch]);

  // Odśwież listę po powrocie na kartę — w tle przeglądarka usypia połączenia
  // Mercure (EventSource) i zdarzenia opublikowane w tym czasie przepadają.
  const lastVisibilityRefreshRef = useRef(0);
  useEffect(() => {
    const refreshOnReturn = () => {
      if (document.visibilityState !== 'visible') return;
      // Nie częściej niż co 3 s — chroni przed serią zdarzeń przy szybkim przełączaniu kart.
      const now = Date.now();
      if (now - lastVisibilityRefreshRef.current < 3000) return;
      lastVisibilityRefreshRef.current = now;
      dispatch(getEventDetails(eventId));
      dispatch(viewEventSongs(eventId));
    };
    document.addEventListener('visibilitychange', refreshOnReturn);
    // pageshow łapie powrót z bfcache (wstecz/naprzód), gdzie visibilitychange może nie zadziałać.
    window.addEventListener('pageshow', refreshOnReturn);
    return () => {
      document.removeEventListener('visibilitychange', refreshOnReturn);
      window.removeEventListener('pageshow', refreshOnReturn);
    };
  }, [eventId, dispatch]);

  const selectEventSongList = createSelector(
    (state) => state.EventSongList,
    (slice) => ({
      eventSongList: slice.list,
      eventSongListMetaData: slice.metaData,
      dedications: slice.dedications,
    })
  );

  const {
    eventSongList,
    eventSongListMetaData,
    dedications,
  } = useSelector(selectEventSongList);

  const limitDedicationsReached = eventSongListMetaData?.limitDedicationsReached ?? false;
  // The dedication group only exists when there is something in it; then the songs
  // get a heading of their own, so the two groups are told apart.
  const showDedications = allowDedications && (isEventAdmin || isEventGuest) && dedications.length > 0;

  // Local state for drag and drop
  const [localSongList, setLocalSongList] = useState([]);

  useEffect(() => {
    setLocalSongList(eventSongList);
  }, [eventSongList]);

  useEffect(() => {
    dispatch(viewEventSongs(eventId));
  }, [dispatch]);

  // DELETE EVENT SONG
  const selectDeleteSongEvent = createSelector(
    (state) => state.EventSongDelete,
    (state) => ({
      deleteEventSongApiSuccess: state.success,
      deleteEventSongApiError: state.errorMessage,
    })
  );

  const {
    deleteEventSongApiSuccess,
    deleteEventSongApiError,
  } = useSelector(selectDeleteSongEvent);

  const [songIdVote, setSongIdVote] = useState(null);

  // Song whose dedication the guest is editing (null = modal closed).
  const [noteEditSong, setNoteEditSong] = useState(null);
  // Full song name shown after clicking "...more" on a truncated row.
  const [fullSongText, setFullSongText] = useState(null);
  // Entry whose dedications the host is reading (null = modal closed).
  const [notesModalSong, setNotesModalSong] = useState(null);
  // Song whose entry the guest is swapping for another one (null = modal closed).
  const [songEditSong, setSongEditSong] = useState(null);

  const handleDeleteItem = (e, id) => {
    e.stopPropagation();
    dispatch(deleteEventSong(id, guestId))
  };

  useEffect(() => {
    if (deleteEventSongApiSuccess) {
      dispatch(viewEventSongs(eventId));
      dispatch(eventSongDeleteReset())
    }
    if (deleteEventSongApiError) {
      notificationService.error(t(deleteEventSongApiError));
      dispatch(eventSongDeleteReset())
    }

  }, [deleteEventSongApiSuccess, deleteEventSongApiError]);

  // REORDER EVENT SONGS
  const selectReorderSongEvent = createSelector(
    (state) => state.EventSongReorder,
    (state) => ({
      reorderEventSongApiSuccess: state.success,
      reorderEventSongApiError: state.errorMessage,
    })
  );

  const {
    reorderEventSongApiSuccess,
    reorderEventSongApiError,
  } = useSelector(selectReorderSongEvent);

  useEffect(() => {
    if (reorderEventSongApiSuccess) {
      notificationService.success(t('Songs reordered successfully.'));
      dispatch(eventSongReorderReset())
    }
    if (reorderEventSongApiError) {
      notificationService.error(t(reorderEventSongApiError));
      dispatch(eventSongReorderReset())
      dispatch(viewEventSongs(eventId));
    }
  }, [reorderEventSongApiSuccess, reorderEventSongApiError]);

  // Drag and Drop Handler
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    // When the list is split into "upcoming"/"pending" sections, map the
    // section-relative index back to an absolute index in the full list.
    const visibleCount = event?.guestVisibleSongsCount || 3;
    const toAbsolute = (droppableId, index) =>
      droppableId === 'pending' ? visibleCount + index : index;
    const fromIdx = toAbsolute(result.source.droppableId, result.source.index);
    const toIdx = toAbsolute(result.destination.droppableId, result.destination.index);
    if (fromIdx === toIdx) return;
    const items = Array.from(localSongList);
    const [reorderedItem] = items.splice(fromIdx, 1);
    items.splice(toIdx, 0, reorderedItem);
    setLocalSongList(items);
    const songIds = items.map(item => item.id);
    dispatch(reorderEventSongs({ eventId, songIds }));
  };

  const [isAddSongModalOpen, setIsAddSongModalOpen] = useState(false);
  const togModalAddSong = () => {
    setIsAddSongModalOpen(!isAddSongModalOpen);
  };

  const [isDedicationModalOpen, setIsDedicationModalOpen] = useState(false);
  const togModalDedication = () => {
    setIsDedicationModalOpen(!isDedicationModalOpen);
  };

  const handleDeleteDedication = (id) => {
    dispatch(deleteDedication(id, guestId));
  };

  const [isModalRegisterAndVoteOpen, setModalRegisterAndVoteOpen] = useState(false);
  const togModalRegisterAndVote = () => {
    setModalRegisterAndVoteOpen(!isModalRegisterAndVoteOpen);
  };

  const [isTipModalOpen, setIsTipModalOpen] = useState(false);
  const stripe = useStripe();
  const resumeAttempted = useRef(false);

  // Bramka "napiwek za utwór": po opłaceniu napiwku wykonujemy odłożone dodanie/głos.
  const [pendingAddPayload, setPendingAddPayload] = useState(null);
  const [pendingGuestName, setPendingGuestName] = useState(null);

  const togTipModal = () => {
    // Zwykły (dobrowolny) napiwek – wyczyść ewentualną bramkę, także tę zapisaną
    // na wypadek przeładowania strony (inaczej nowa płatność dopięłaby się do
    // porzuconej wcześniej intencji dodania utworu).
    setPendingAddPayload(null);
    setPendingGuestName(null);
    clearPendingTipAction();
    setIsTipModalOpen((open) => !open);
  };

  // Opis odłożonej akcji dla serwera. Utwór i dedykacja mają znanego gościa, więc
  // doda je webhook/zapis napiwku — bez udziału przeglądarki. Głos zostaje po stronie
  // klienta: rejestruje przy okazji nowego gościa, którego id musi wrócić do tej karty.
  const buildPendingAction = (payload) => {
    if (payload.text !== undefined) {
      return { type: 'dedication', userId: payload.id, text: payload.text };
    }

    if (payload.user?.id == null) {
      return null;
    }

    return {
      type: 'song',
      userId: payload.user.id,
      songId: payload.song?.id ?? null,
      artistTitle: payload.song?.artistTitle ?? null,
      guestNote: payload.guestNote ?? null,
    };
  };

  // Wywoływane przez przyciski dodania/głosu gdy wymagany jest napiwek.
  const payAndAdd = (payload, guestName = null) => {
    setPendingAddPayload(payload);
    setPendingGuestName(guestName);
    // Poza stanem Reacta, bo telefon potrafi zamknąć kartę na czas potwierdzania
    // BLIK-a w aplikacji banku — po powrocie dokończymy dodawanie z localStorage.
    savePendingTipAction(eventId, payload, guestName);
    setIsAddSongModalOpen(false);
    setIsDedicationModalOpen(false);
    setModalRegisterAndVoteOpen(false);
    setIsTipModalOpen(true);
  };

  const closeTipModal = () => {
    setPendingAddPayload(null);
    setPendingGuestName(null);
    clearPendingTipAction();
    setIsTipModalOpen(false);
  };

  // Wykonanie odłożonej akcji rozpoznanej po kształcie ładunku.
  const runPendingAction = (payload) => {
    if (payload.text !== undefined) {
      // Odłożona dedykacja (bez utworu) — rozpoznajemy ją po polu text.
      dispatch(addDedication(payload));
    } else if (payload.user?.id == null) {
      // Nowy gość głosujący – rejestracja obsłużona w voteSongGuest.
      dispatch(voteSongGuest(payload));
    } else {
      dispatch(addSongGuest(payload));
    }
  };

  // Po udanej płatności bramkowej. Gdy serwer przyjął odłożoną akcję (utwór,
  // dedykacja), utwór jest już na liście — dodanie go tutaj zrobiłoby duplikat,
  // więc tylko odświeżamy widok. Głos dodajemy po staremu, z przeglądarki.
  const handleTipGateSuccess = () => {
    if (!pendingAddPayload) return;

    if (loadPendingTipAction()?.serverSide) {
      dispatch(viewEventSongs(eventId));
    } else {
      runPendingAction(pendingAddPayload);
    }

    clearPendingTipAction();
    setPendingAddPayload(null);
    setPendingGuestName(null);
  };

  // Powrót z aplikacji banku po tym, jak telefon usunął uśpioną kartę z pamięci:
  // strona wczytuje się od zera, więc o odłożonym utworze wie już tylko localStorage.
  // Jeśli Stripe potwierdza płatność, dokańczamy to, za co gość zapłacił.
  useEffect(() => {
    if (!stripe || resumeAttempted.current) return;

    const pending = loadPendingTipAction();
    if (!pending || pending.eventId !== eventId || !pending.clientSecret) return;

    resumeAttempted.current = true;
    let cancelled = false;

    (async () => {
      const result = await verifyPendingTipPayment(stripe, pending);
      if (cancelled) return;

      // 'pending' = płatność wciąż czeka na potwierdzenie w banku; zostawiamy ją
      // w localStorage, żeby dokończyć przy następnym wejściu na stronę.
      if (result === 'pending') return;

      clearPendingTipAction();

      if (result === 'paid') {
        // serverSide: dodał to webhook albo zapis napiwku — wystarczy odświeżyć listę.
        if (pending.serverSide) {
          dispatch(viewEventSongs(eventId));
        } else {
          runPendingAction(pending.payload);
        }
        notificationService.success(t('Payment successful! Thank you for your tip!'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stripe, eventId]);

  if (Object.keys(event).length === 0) {
    return <Spinner setLoading={() => {}} />;
  }

  const renderSongRow = (eventSong, index, provided = null, snapshot = null) => {
    const isKaraoke = type === typeKaraoke;
    const isGuestSong = isEventGuest && guestId === eventSong.userId;
    const canVote = !isEventAdmin && !isGuestSong && !isKaraoke;
    const isDragging = snapshot?.isDragging;

    // Standard events collapse every request for the same song into one entry, so a
    // row can carry several dedications; guests only ever get their own back.
    const notes = eventSong.guestNotes?.length
      ? eventSong.guestNotes
      : (eventSong.guestNote ? [eventSong.guestNote] : []);
    return (
      <div
        className="event-song-row song ps-2 p-1"
        ref={provided?.innerRef}
        {...(provided?.draggableProps || {})}
        style={{
          ...provided?.draggableProps?.style,
          background: isDragging
            ? '#707273'
            : isGuestSong
              ? 'rgba(var(--vz-success-rgb), 0.25)'
              : 'transparent',
          borderRadius: isGuestSong ? '8px' : undefined,
        }}
      >
        {isClubAdmin && isKaraoke && (
          <div
            {...(provided?.dragHandleProps || {})}
            className="me-2 flex-shrink-0"
            style={{ cursor: 'grab', touchAction: 'none' }}
          >
            <i className="ri-draggable" style={{ fontSize: '1.2rem', color: '#878a99' }}></i>
          </div>
        )}
        {!isKaraoke && eventSong.rate !== 0 && (
          <span className="d-inline-flex align-items-center text-warning me-2 fw-bold flex-shrink-0">
            <i className="ri-thumb-up-line me-1" style={{ fontSize: '1.15rem' }}></i>{eventSong.rate}
          </span>
        )}
        <div className="event-song-text">
          {!isKaraoke && (
            <SongTextTruncate fullText={`${eventSong.title} - ${eventSong.artist}`} onShowFull={setFullSongText}>
              <div className="d-inline">{eventSong.title} </div>
              <div className="d-inline contact-born text-muted mb-0"> - {eventSong.artist}</div>
              {isClubAdmin && eventSong.username && eventSong.username !== "undefined" && (
                <span className="mb-125 mt-125 badge badge-newline bg-warning-subtle text-warning">
                  {eventSong.username}
                </span>
              )}
            </SongTextTruncate>
          )}
          {isKaraoke && (
            <SongTextTruncate fullText={`${eventSong.title} - ${eventSong.artist}`} onShowFull={setFullSongText}>
              <div className="d-inline">{eventSong.title} </div>
              <div className="d-inline contact-born text-muted mb-0"> - {eventSong.artist}</div>
              {eventSong.username && eventSong.username !== "undefined" && (
                <span className={isGuestSong ? "badge badge-newline bg-success-subtle text-warning mb-125 mt-125" : "mb-125 mt-125 badge badge-newline bg-warning-subtle text-warning"}>
                  {eventSong.username}
                </span>
              )}
            </SongTextTruncate>
          )}
        </div>
        {!isDragging && (
          <ButtonGroup className="gap-1 flex-shrink-0 ms-2">
            {canVote && (
              <VoteSongButton
                eventSongListMetaData={eventSongListMetaData}
                isEventGuest={isEventGuest}
                songId={eventSong.songId}
                eventId={eventId}
                guestId={guestId}
                setModalRegisterAndVoteOpen={setModalRegisterAndVoteOpen}
                setSongIdVote={setSongIdVote}
                eventType={type}
                requireTip={requireTip}
                onPay={payAndAdd}
              />
            )}
            {/* Host only: the guest reads and rewrites their own note in the envelope
                modal, so a second button pointing at the same text is noise. */}
            {notes.length > 0 && isEventAdmin && (
              <button
                type="button"
                className="btn btn-sm btn-icon text-white"
                onClick={() => setNotesModalSong(eventSong)}
                title={isKaraoke ? t('Note for the Host') : t('Dedication')}
              >
                <i className="ri-sticky-note-line fs-20"></i>
              </button>
            )}
            {/* Only the owner, and only once the song is past the first position. */}
            {isGuestSong && eventSong.position >= 2 && (
              <button
                type="button"
                className={`btn btn-sm btn-icon ${eventSong.guestNote ? 'text-success' : 'text-white'}`}
                onClick={() => setNoteEditSong(eventSong)}
                title={isKaraoke ? t('Note for the Host') : t('Dedication')}
              >
                <i className={`${eventSong.guestNote ? 'ri-mail-line' : 'ri-mail-add-line'} fs-20`}></i>
              </button>
            )}
            {isGuestSong && eventSong.position >= 2 && (
              <button
                type="button"
                className="btn btn-sm btn-icon text-white"
                onClick={() => setSongEditSong(eventSong)}
                title={t('Change song')}
              >
                <i className="ri-pencil-line fs-20"></i>
              </button>
            )}
            {(isEventAdmin || isGuestSong) && (
              <div className="remove">
                <button
                  className="btn btn-sm btn-icon text-white"
                  onClick={(e) => handleDeleteItem(e, eventSong.id)}
                >
                  <i className="ri-indeterminate-circle-line fs-20"></i>
                </button>
              </div>
            )}
          </ButtonGroup>
        )}
      </div>
    );
  };

  // Partially hidden list: guests see a limited number of "upcoming" songs plus
  // their own "pending" songs; admins see the same split but with all pending songs.
  const isStaff = isClubAdmin || isAdmin;
  const partiallyHidden = !!event?.partiallyHiddenForGuests;
  const visibleCount = event?.guestVisibleSongsCount || 3;
  const isKaraokeAdmin = isClubAdmin && type === typeKaraoke;

  const upcomingSongs = partiallyHidden ? localSongList.slice(0, visibleCount) : localSongList;
  let pendingSongs = [];
  if (partiallyHidden) {
    pendingSongs = localSongList.slice(visibleCount);
    if (!isStaff) {
      // "Oczekujące utwory" is the admin's private bucket: the admin can reorder
      // it freely without guests noticing. Guests only see changes on the
      // "upcoming" list, so their own pending songs keep a stable order (order
      // added = ascending EventSong id) regardless of how the admin arranges them.
      pendingSongs = pendingSongs
        .filter((s) => guestId && s.userId === guestId)
        .sort((a, b) => a.id - b.id);
    }
  }

  const renderPlainTable = (songs, indexOffset = 0) => (
    <div className="event-song-list list form-check-all">
      {songs.map((eventSong, i) => (
        <React.Fragment key={eventSong.id}>
          {renderSongRow(eventSong, indexOffset + i)}
        </React.Fragment>
      ))}
    </div>
  );

  const renderDraggableTable = (songs, droppableId, indexOffset = 0) => (
    <Droppable droppableId={droppableId}>
      {(provided) => (
        <div
          className="event-song-list list form-check-all"
          {...provided.droppableProps}
          ref={provided.innerRef}
        >
          {songs.map((eventSong, i) => (
            <Draggable key={eventSong.id} draggableId={String(eventSong.id)} index={i}>
              {(provided, snapshot) => renderSongRow(eventSong, indexOffset + i, provided, snapshot)}
            </Draggable>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );

  const sectionTitle = (label) => (
    <h6 className="text-muted fw-bold text-uppercase mb-1 mt-2 ps-1">{label}</h6>
  );

  return (
    <React.Fragment>
      <div className={`page-content event-page-content ${!isAdmin ? 'non-admin-layout' : ''}`}>
        <Container className="ps-0 pe-0" fluid>
          <Row>
            <Col lg={12}>
              <Card className="bg-transparent">
                <CardHeader className="border-bottom-0 bg-transparent ps-1 pe-1 pb-0">
                  <EventHeader isEventAdmin={isEventAdmin} event={event} />
                  {!startAt && !isEventAdmin && <EventClosedAlert/>}
                </CardHeader>
                <CardBody className="pt-2">
                  <div className="listjs-table" id="songList">
                    <Row className="g-4 mb-1">
                      <div className="ps-1">
                        {/* Gdy przyciski nie mieszczą się w jednej linii, ten zawijany
                            spada niżej - własne odstępy przycisków w zupełności
                            wystarczą, więc odstęp między wierszami zerujemy. */}
                        <div className="d-flex flex-wrap" style={{ columnGap: '0.25rem', rowGap: 0 }}>
                          {isEventAdmin && <StartEventButton eventId={eventId} startAt={startAt} />}
                          {startAt && allowAddSongs && (isEventGuest || isClubAdmin) && (
                            <AddSongButton
                              togModalAddSong={togModalAddSong}
                              eventType={type}
                              eventSongListMetaData={eventSongListMetaData}
                            />
                          )}
                          {startAt && allowDedications && (isEventGuest || isClubAdmin) && !isEventAdmin && (
                            <button
                              type="button"
                              className="btn bg-transparent border-0 shadow-none color-primary d-inline-flex align-items-center px-2 py-0 me-1"
                              onClick={togModalDedication}
                              disabled={limitDedicationsReached}
                              title={limitDedicationsReached ? t('Your dedication limit has been reached') : undefined}
                            >
                              <i className="ri-mail-add-line align-middle me-2" style={{ fontSize: '1.6rem' }}></i>
                              {t('Dedication')}
                            </button>
                          )}
                          {startAt && !isClubAdmin && isEventGuest && allowTips && !requireTip && (
                            <button
                              type="button"
                              className="btn bg-transparent border-0 shadow-none color-primary d-inline-flex align-items-center px-2 py-0"
                              onClick={togTipModal}
                            >
                              <i className="ri-hand-coin-line align-middle me-2" style={{ fontSize: '1.6rem' }}></i>
                              {t('Tip')}
                            </button>
                          )}
                        </div>
                      </div>
                    </Row>
                    <div className="table-responsive table-card mt-1 mb-1 ps-1 pe-1">
                      {showDedications && localSongList.length > 0 && !partiallyHidden && sectionTitle(t('Songs'))}
                      {localSongList.length > 0 && !partiallyHidden && (
                        isKaraokeAdmin ? (
                          <DragDropContext onDragEnd={handleDragEnd}>
                            {renderDraggableTable(localSongList, 'eventSongs', 0)}
                          </DragDropContext>
                        ) : (
                          renderPlainTable(localSongList, 0)
                        )
                      )}
                      {localSongList.length > 0 && partiallyHidden && (
                        isKaraokeAdmin ? (
                          <DragDropContext onDragEnd={handleDragEnd}>
                            {sectionTitle(t('Upcoming songs'))}
                            {renderDraggableTable(upcomingSongs, 'upcoming', 0)}
                            {sectionTitle(isStaff ? t('Pending songs') : t('My pending songs'))}
                            {pendingSongs.length > 0
                              ? renderDraggableTable(pendingSongs, 'pending', visibleCount)
                              : <p className="text-muted ps-1 mb-2">{t('No pending songs.')}</p>}
                          </DragDropContext>
                        ) : (
                          <>
                            {sectionTitle(t('Upcoming songs'))}
                            {renderPlainTable(upcomingSongs, 0)}
                            {sectionTitle(isStaff ? t('Pending songs') : t('My pending songs'))}
                            {pendingSongs.length > 0
                              ? renderPlainTable(pendingSongs, visibleCount)
                              : <p className="text-muted ps-1 mb-2">{isStaff ? t('No pending songs.') : t('You have no pending songs.')}</p>}
                          </>
                        )
                      )}
                      {startAt && showDedications && (
                        <>
                          {sectionTitle(isEventAdmin ? t('Dedications') : t('My dedications'))}
                          <div className="event-song-list list">
                            {dedications.map((dedication) => (
                              <div key={dedication.id} className="event-song-row song ps-2 p-1">
                                <div className="event-song-text">
                                  <div className="text-break">{dedication.text}</div>
                                  {isEventAdmin && dedication.username && dedication.username !== "undefined" && (
                                    <span className="mb-125 mt-125 badge badge-newline bg-warning-subtle text-warning">
                                      {dedication.username}
                                    </span>
                                  )}
                                </div>
                                <ButtonGroup className="gap-1 flex-shrink-0 ms-2">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-icon text-white"
                                    onClick={() => handleDeleteDedication(dedication.id)}
                                    title={t('Delete')}
                                  >
                                    <i className="ri-indeterminate-circle-line fs-20"></i>
                                  </button>
                                </ButtonGroup>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      {startAt && localSongList.length === 0 && (
                        <div className="text-center py-2">
                          <i
                            className="ri-music-2-line text-muted"
                            style={{ fontSize: "3rem" }}
                          ></i>
                          <h5 className="mt-3 text-muted">
                            {allowAddSongs && (isEventGuest || isClubAdmin) && <div><span className="fw-bolder">{t("The list is empty.")}</span> <br/><br/> {t("Add the first song by clicking the button above.")}</div>}
                            {!allowAddSongs && <div><span className="fw-bolder">{t("The list is still empty.")}</span> <br/><br/> {t("Wait for the first song.")}</div>}
                            {allowAddSongs && !isEventGuest && !isClubAdmin && <div><span className="fw-bolder">{t("The list is empty.")}</span> <br/><br/> {t("Register to add songs.")}</div>}
                          </h5>
                        </div>
                      )}
                      {isClubAdmin && !startAt && localSongList.length === 0 && (
                        <div className="text-center py-2">
                          <i
                            className="ri-music-2-line text-muted"
                            style={{ fontSize: "3rem" }}
                          ></i>
                          <h5 className="mt-3 text-muted">
                            <div><span className="fw-bolder">{t("Your list is empty.")}</span> <br/><br/> {t("Start the event so your guests can add songs or create the list yourself.")}</div>
                          </h5>
                        </div>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Modal rejestracji gościa - wyświetlany automatycznie przy wejściu */}
      <RegisterGuestModal
        isOpen={isRegisterModalOpen}
        toggle={togRegisterModal}
        eventId={eventId}
      />

      {/* Modal dodawania piosenki - tylko dla zarejestrowanych gości */}
      <AddEventSongModal
        event={event}
        eventId={eventId}
        eventStartAt={startAt}
        togModalAddSong={togModalAddSong}
        isAddSongModalOpen={isAddSongModalOpen}
        isClubAdmin={isClubAdmin}
        guestId={guestId}
        isEventAdmin={isEventAdmin}
        eventType={type}
        requireTip={requireTip}
        onPay={payAndAdd}
      />

      <VoteModal
        songId={songIdVote}
        eventId={eventId}
        isEventGuest={isEventGuest}
        togModalRegisterAndVote={togModalRegisterAndVote}
        isModalRegisterAndVoteOpen={isModalRegisterAndVoteOpen}
        isClubAdmin={isClubAdmin}
        guestId={guestId}
        isEventAdmin={isEventAdmin}
        eventType={type}
        requireTip={requireTip}
        onPay={payAndAdd}
      />

      <AddDedicationModal
        isOpen={isDedicationModalOpen}
        toggle={togModalDedication}
        eventId={eventId}
        guestId={guestId}
        requireTip={requireTip && !isClubAdmin}
        onPay={payAndAdd}
      />

      <SongFullNameModal text={fullSongText} onClose={() => setFullSongText(null)} />

      <SongNotesModal
        eventSong={notesModalSong}
        isKaraoke={type === typeKaraoke}
        onClose={() => setNotesModalSong(null)}
      />

      <EditEventSongModal
        eventSong={songEditSong}
        event={event}
        eventId={eventId}
        guestId={guestId}
        onClose={() => setSongEditSong(null)}
      />

      <EditSongNoteModal
        eventSong={noteEditSong}
        eventId={eventId}
        guestId={guestId}
        isKaraoke={type === typeKaraoke}
        onClose={() => setNoteEditSong(null)}
      />

      {!isClubAdmin && <TipHostModal
        isOpen={isTipModalOpen}
        toggle={closeTipModal}
        eventId={eventId}
        adminUsername={adminUsername}
        hostPhoneNumber={event?.clubAdmin?.phoneNumber || null}
        minAmount={pendingAddPayload ? minTipAmount : 2}
        guestName={pendingAddPayload ? pendingGuestName : undefined}
        onSuccess={pendingAddPayload ? handleTipGateSuccess : undefined}
        pendingAction={pendingAddPayload ? buildPendingAction(pendingAddPayload) : undefined}
      />
      }

    </React.Fragment>
  );
};

export default EventSongs;
